import dayjs, { Dayjs } from 'dayjs'
import {
  HealthcareService,
  QuestionnaireResponseItem,
  Reference,
  ServiceRequest
} from '@medplum/fhirtypes'
import { QuestionnaireResponse } from '@medplum/fhirtypes'
import {
  BaseConsentFragment,
  BaseQuestionnaireResponseFragment,
  BaseQuestionnaireWithResponseListFragment
} from 'generated/graphql'
import { AssessmentType } from 'components/Clinical/Assessments/constants'
import {
  MedicationInfo,
  MedicationStatus,
  MedicationUsageStatus
} from 'components/Common/Medication/types'
import {
  CONSENT_ACTOR_ROLE_CODE_SYSTEM,
  ConsentActorRoleCode,
  ConsentChartingCategory
} from 'fhir/Consent/constants'
import { getAuthorizationChartingCategory } from 'fhir/Consent/helpers'
import {
  SERVICE_REQUEST_CHARTING_CATEGORY_EXTENSION_URL,
  SERVICE_REQUEST_SERVICE_TYPE_EXTENSION,
  ServiceRequestChartingCategory
} from 'fhir/ServiceRequest/constants'
import {
  LOINC_CODE_PRIOR_AUTH,
  LOINC_CODE_PRIOR_AUTH_REQUEST
} from 'fhir/ServiceRequest/categories'
import { checkFeatureConfigByServiceTypes } from 'utils/featureConfig'
import { CustomFeatureConfigKey } from 'utils/featureConfig/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { ORDERS_FOR_TREATMENT_HEADER } from './useNarrative'
import { InterventionInfo, SharedCareQuestionnaireItem } from './constants'
import { SocAssessmentQuestionnaireItem } from '../SocAssessment/types/questionnaire'
import { RecertAssessmentQuestionnaireItem } from '../RecertAssessment/types/questionnaire'
import {
  getAnswerValuesFromItems,
  InterventionInfoWithDuration,
  mapCustomVisitsToInterventions
} from '../helpers'
import {
  AssessmentAuthorizationSource,
  AuthorizationSource
} from '../components/Authorization/types'

// Returns the linkId for a soc assessment item
export const SocQuestionnaireItem = (
  key: keyof typeof SocAssessmentQuestionnaireItem
): SocAssessmentQuestionnaireItem => {
  return SocAssessmentQuestionnaireItem[key]
}

// Returns the linkId for a recert assessment item
export const RecertQuestionnaireItem = (
  key: keyof typeof RecertAssessmentQuestionnaireItem
): RecertAssessmentQuestionnaireItem => {
  return RecertAssessmentQuestionnaireItem[key]
}

export const getLatestResponseFromQuestionnaire = (
  questionnairesWithResponseList: BaseQuestionnaireWithResponseListFragment[],
  { currentAssessmentId }: { currentAssessmentId?: string } = {}
): BaseQuestionnaireResponseFragment | null => {
  const allQuestionnairesWithLatestResponse = questionnairesWithResponseList.map(
    (questionnaire) => {
      // Filters out the questionnaire response that is the assessment we're currently editing.
      const fullyCompletedQuestionnaireResponseList =
        questionnaire?.QuestionnaireResponseList?.filter((r) => {
          if (r?.id === currentAssessmentId) return false

          const assessmentItems = r?.item as QuestionnaireResponseItem[] | undefined
          if (!assessmentItems) return false

          return true
        }) ?? []

      const latestValidQuestionnaireResponse = fullyCompletedQuestionnaireResponseList
        ?.sort((a, b) => dayjs(b?.authored ?? 0).valueOf() - dayjs(a?.authored ?? 0).valueOf())
        ?.find((q) => !!q)

      return latestValidQuestionnaireResponse
    }
  )
  const latestResponse = allQuestionnairesWithLatestResponse?.find((q) => !!q)

  return latestResponse ?? null
}

export interface OtherAppointmentInfo {
  name?: string
  periodStart?: string
  periodEnd?: string
  repeatsOnDays?: string[]
}

export interface VisitInfo {
  periodStart?: string
  periodEnd?: string
  periodCaregiver?: string
}

export interface InterventionEditDropdownParams {
  interventionId?: number
  interventionGoal?: string
  type?: string
  indication?: string
  instruction?: string
  patientCenteredGoal?: string
  additionalDetails?: string
  visitNumbers?: number[]
  frequency?: string[]
}

export interface InterventionForVisit {
  intervention: InterventionInfo
  index: number
}

export const getInterventionsForVisit = (
  interventions: InterventionInfo[],
  visitNumber: number
): InterventionForVisit[] => {
  return interventions
    .map((intervention, index) => ({ intervention, index }))
    .filter(
      ({ intervention }) =>
        !intervention.visitNumbers ||
        intervention.visitNumbers.length === 0 ||
        intervention.visitNumbers.includes(visitNumber)
    )
}

export const getFrequencyForIntervention = (intervention: InterventionInfo): string => {
  return intervention.frequency && intervention.frequency.length > 0
    ? intervention.frequency.join('/')
    : 'Daily'
}

export const formatAssessmentInterventions = (
  flatAnswers: Record<string, QuestionnaireResponseItem>
): InterventionInfo[] => {
  const interventionInfoQuestionnaireAnswer =
    flatAnswers[SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO')]
      ?.answer ?? []

  return interventionInfoQuestionnaireAnswer.map((interventionInfoAnswer): InterventionInfo => {
    const interventionInfoItem = interventionInfoAnswer.item ?? []
    const [
      [id],
      [category],
      [interventionGoal],
      [task],
      [indication],
      [type],
      [instruction],
      [patientCenteredGoal],
      [additionalDetails],
      visitNumbers,
      frequency
    ] = [
      SharedCareQuestionnaireItem(
        'PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION'
      ),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY'),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL'),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK'),
      SharedCareQuestionnaireItem(
        'PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION'
      ),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE'),
      SharedCareQuestionnaireItem(
        'PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION'
      ),
      SharedCareQuestionnaireItem(
        'PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL'
      ),
      SharedCareQuestionnaireItem(
        'PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS'
      ),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS'),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY')
    ].map((linkId) => getAnswerValuesFromItems(interventionInfoItem, linkId))

    return {
      id,
      category,
      interventionGoal,
      task,
      indication,
      type,
      instruction,
      patientCenteredGoal,
      additionalDetails,
      visitNumbers,
      frequency
    }
  })
}

export const formatAssessmentInterventionsText = (
  flatAnswers: Record<string, QuestionnaireResponseItem>,
  authPatientServiceTypeNames: ServiceTypeName[] | undefined
): string | null => {
  const interventions = formatAssessmentInterventions(flatAnswers)
  if (!interventions.length || !authPatientServiceTypeNames) return null

  const areAideTasksEnabled = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.AreAideTasks,
    authPatientServiceTypeNames
  )

  const hasTasksFrequencyEnabled = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.HasTasksFrequency,
    authPatientServiceTypeNames
  )

  if (areAideTasksEnabled) {
    return formatAideTasks(interventions, hasTasksFrequencyEnabled)
  } else {
    const interventionsByVisitNumber = mapCustomVisitsToInterventions(flatAnswers, interventions)
    return formatVisitInterventions(interventionsByVisitNumber)
  }
}

export const formatAssessmentMedications = (
  flatAnswers: Record<string, QuestionnaireResponseItem>
): MedicationInfo[] => {
  const medicationInfoQuestionnaireAnswer =
    flatAnswers[SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO')]
      ?.answer ?? []

  return medicationInfoQuestionnaireAnswer.map((medicationInfoAnswer): MedicationInfo => {
    const medicationInfoItem = medicationInfoAnswer.item ?? []

    const [
      [drugbankPcId],
      [name],
      [route],
      [strengthUnit],
      [indications],
      [frequency],
      [dosageUnit],
      [instructions],
      [controlled],
      [liquid],
      [infection],
      [strengthNumber],
      [dosageNumber],
      [usageStatus],
      [startDate],
      [discontinueDate]
    ] = [
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DRUGBANK_PCID'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_NAME'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_ROUTE'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_UNIT'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INDICATIONS'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_FREQUENCY'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_UNIT'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INSTRUCTIONS'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_CONTROLLED'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_LIQUID'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INFECTION'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_NUMBER'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_NUMBER'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_USAGE_STATUS'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_START_DATE'),
      SharedCareQuestionnaireItem('MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DISCONTINUE_DATE')
    ].map((linkId) => getAnswerValuesFromItems(medicationInfoItem, linkId))

    return {
      productConceptId: drugbankPcId,
      status: discontinueDate?.isBefore(dayjs(), 'day')
        ? MedicationStatus.INACTIVE
        : MedicationStatus.ACTIVE,
      name,
      route,
      strength: {
        number: strengthNumber,
        unit: strengthUnit
      },
      indications,
      frequency,
      dosage: {
        number: dosageNumber,
        unit: dosageUnit
      },
      instructions,
      usageStatus,
      startDate,
      discontinueDate,
      controlled,
      liquid,
      infection
    }
  })
}

export const MULTIPLE_VALUE_SEPARATOR = '  |  '
export const formatDiagnosesFormValues = ({
  code,
  description,
  startDate,
  endDate
}: {
  code?: string
  description?: string
  startDate: Dayjs
  endDate: Dayjs | null
}): string => {
  return [
    `Code: ${code ?? ''}`,
    `Code Description: ${description ?? ''}`,
    `Diagnosis Date: ${startDate?.format('YYYY-MM-DD')}${
      endDate ? ` - ${endDate.format('YYYY-MM-DD')}` : ''
    }`
  ].join(MULTIPLE_VALUE_SEPARATOR)
}

export const generateMedicationsFormValues = (
  flatAnswers: Record<string, QuestionnaireResponseItem>
): string => {
  const medications = formatAssessmentMedications(flatAnswers)
  if (medications.length === 0) return ''

  const activeMedications = medications.filter(
    (medication) => medication.status === MedicationStatus.ACTIVE
  )

  const joinedMedicationRows = parseMedicationSectionContent(activeMedications)

  return joinedMedicationRows
}

const parseMedicationSectionContent = (medication: MedicationInfo[]): string => {
  return medication
    .map((medication, i) => {
      const medicationRow: string[] = []

      let medicationLabel = `Name: ${medication.name}`
      if (
        medication.usageStatus === MedicationUsageStatus.NEW ||
        medication.usageStatus === MedicationUsageStatus.CHANGED
      ) {
        medicationLabel = `(${medication.usageStatus.toUpperCase()}) ${medicationLabel}`
      }
      medicationRow.push(medicationLabel)

      if (medication.dosage?.unit && medication.dosage?.number) {
        medicationRow.push(
          `Dosage: ${medication.dosage.number.toString()} ${medication.dosage.unit}`
        )
      }
      if (medication.frequency) {
        medicationRow.push(`Frequency: ${medication.frequency}`)
      }
      if (medication.route) {
        medicationRow.push(`Route: ${medication.route}`)
      }

      if (medication.instructions) {
        medicationRow.push(`Instructions: ${medication.instructions}`)
      }

      if (medication.indications) {
        medicationRow.push(`Indications: ${medication.indications}`)
      }

      if (medication.startDate) {
        medicationRow.push(
          `Start Date: ${medication.startDate ? medication.startDate.format('YYYY-MM-DD') : null}`
        )
      }

      if (medication.discontinueDate) {
        medicationRow.push(
          `Discontinue Date: ${
            medication.discontinueDate ? medication.discontinueDate.format('YYYY-MM-DD') : null
          }`
        )
      }

      return `${i + 1}. ${medicationRow.join(MULTIPLE_VALUE_SEPARATOR)}`
    })
    .join('\n\n')
}

export const formatVisitInterventions = (
  interventionsByVisitNumber: Record<string, InterventionInfoWithDuration>
): string => {
  const interventionTexts = Object.keys(interventionsByVisitNumber).map((visitNumber) => {
    const { duration, interventions } = interventionsByVisitNumber[visitNumber]
    const interventionsForVisitText = [`Visit ${visitNumber} (${duration})`]
    const interventionsLabels = interventions.map((intervention) => {
      const { task, type, indication, frequency } = intervention

      const taskFrequency =
        frequency && frequency.length > 0 ? ` (${frequency.join(', ')})` : ' (Daily)'
      return `\t- Task: ${task}${MULTIPLE_VALUE_SEPARATOR}Type: ${type}${taskFrequency}${MULTIPLE_VALUE_SEPARATOR}Indication: ${indication}`
    })

    interventionsForVisitText.push(...interventionsLabels)
    return interventionsForVisitText.join('\n')
  })

  return interventionTexts.join('\n\n')
}

export const formatAideTasks = (
  interventions: InterventionInfo[],
  hasTaskFrequencyEnabled: boolean
): string => {
  const interventionsText = ['Aide Tasks']
  interventionsText.push(
    ...interventions.map((intervention) => {
      const { task, type, indication, frequency } = intervention
      const taskFrequency = frequency && frequency.length > 0 ? `${frequency.join(', ')}` : 'Daily'

      return `\t- Task: ${task}${MULTIPLE_VALUE_SEPARATOR}Type: ${type}${MULTIPLE_VALUE_SEPARATOR}Indication: ${indication}${
        hasTaskFrequencyEnabled ? `${MULTIPLE_VALUE_SEPARATOR}Frequency: ${taskFrequency}` : ''
      }`
    })
  )
  return interventionsText.join('\n')
}

// Separates orders for treatment section from care coordination
export function formatCareCoordinationAnswer(answer?: string): {
  ordersForTreatment: string | null
  careCoordination: string | null
} {
  if (!answer) return { ordersForTreatment: null, careCoordination: null }
  const answerLines = answer.split(ORDERS_FOR_TREATMENT_HEADER)
  return {
    ordersForTreatment: `${ORDERS_FOR_TREATMENT_HEADER}${answerLines.at(1)}`,
    careCoordination: answerLines.at(0) ?? ''
  }
}

export interface GenerateAssessmentDocumentFunctionParams {
  assessmentId: string
  assessmentType: AssessmentType
  response: QuestionnaireResponse
  flatAnswers: Record<string, QuestionnaireResponseItem>
  isRegeneratedDocument?: boolean
  flowId?: string
}

export function getAuthorizationHealthcareServiceReferences(
  authorizationSource: AssessmentAuthorizationSource | null
): string[] {
  if (!authorizationSource) {
    return []
  }

  if (authorizationSource.resourceType === 'ServiceRequest') {
    const serviceTypeRef = authorizationSource.extension?.find(
      (extension) => extension.url === SERVICE_REQUEST_SERVICE_TYPE_EXTENSION
    )?.valueReference?.reference

    return serviceTypeRef ? [serviceTypeRef] : []
  }

  if (authorizationSource.resourceType === 'Consent') {
    return (
      authorizationSource.provision?.actor
        ?.filter((actor) =>
          actor.role?.coding?.some(
            (coding) =>
              coding.system === CONSENT_ACTOR_ROLE_CODE_SYSTEM &&
              coding.code === ConsentActorRoleCode.PERFORMER
          )
        )
        ?.map((actor) => actor.reference?.reference)
        ?.filter(
          (reference): reference is string => !!reference?.startsWith('HealthcareService/')
        ) ?? []
    )
  }

  return []
}

export function getChartingCategoryForAuthorizationSource(
  authorizationSource: AssessmentAuthorizationSource
): ServiceRequestChartingCategory {
  if (authorizationSource.resourceType === 'ServiceRequest') {
    const order = authorizationSource as ServiceRequest
    const isAuthorizationTypeOrder = order.code?.coding?.some(
      (coding) =>
        coding.code && [LOINC_CODE_PRIOR_AUTH, LOINC_CODE_PRIOR_AUTH_REQUEST].includes(coding.code)
    )
    if (isAuthorizationTypeOrder) {
      const chartingCategory = order.extension?.find(
        (extension) => extension.url === SERVICE_REQUEST_CHARTING_CATEGORY_EXTENSION_URL
      )?.valueCode

      if (
        chartingCategory === ServiceRequestChartingCategory.VISIT ||
        chartingCategory === ServiceRequestChartingCategory.STANDARD_SHIFT
      ) {
        return chartingCategory
      }

      return ServiceRequestChartingCategory.STANDARD_SHIFT
    }
  }

  if (authorizationSource.resourceType === 'Consent') {
    const chartingCategory = getAuthorizationChartingCategory(authorizationSource)
    if (chartingCategory === ConsentChartingCategory.VISIT) {
      return ServiceRequestChartingCategory.VISIT
    }
    if (chartingCategory === ConsentChartingCategory.STANDARD_SHIFT) {
      return ServiceRequestChartingCategory.STANDARD_SHIFT
    }
  }

  return ServiceRequestChartingCategory.VISIT
}

/**
 * Extracts service type information from an authorization resource
 * @param authorizationSource - The authorization resource (ServiceRequest or Consent)
 * @returns Array of service type options with label and value
 */
export function extractServiceTypesFromAuthorization(
  serviceTypes: HealthcareService[],
  authorizationSource: AssessmentAuthorizationSource | null
): HealthcareService[] {
  if (!authorizationSource) return []

  const serviceTypeRefs = getAuthorizationHealthcareServiceReferences(authorizationSource)
  const selectedRefs =
    authorizationSource.resourceType === 'Consent' ? serviceTypeRefs.slice(0, 1) : serviceTypeRefs

  const selectedServiceTypes = selectedRefs
    .map((serviceTypeRef) => {
      const serviceType = serviceTypes.find(
        (serviceType) => `HealthcareService/${serviceType.id}` === serviceTypeRef
      )

      if (serviceType) {
        return serviceType
      }
    })
    .filter((serviceType): serviceType is HealthcareService => serviceType !== undefined)

  return Array.from(new Set(selectedServiceTypes))
}

// Validate that the selected Authorization Source options are not a mix of authorizationConsents and priorAuthorizationRequestOrderSources
export function validateNotMixedAuthorizationSources(
  selectedOptionValues: string[],
  authorizationConsents: AuthorizationSource[],
  priorAuthorizationRequestOrderSources: AuthorizationSource[]
): boolean {
  const isFromAuthorizationConsents = selectedOptionValues.every((value) =>
    authorizationConsents.some(({ reference }) => value === reference)
  )

  const isFromPriorAuthorizationRequests = selectedOptionValues.every((value) =>
    priorAuthorizationRequestOrderSources.some(({ reference }) => value === reference)
  )

  return isFromAuthorizationConsents || isFromPriorAuthorizationRequests
}

// Validate that the selected Authorization Source options are not multiple authorizations with the same service type
export function validateNoDuplicateServiceTypes(
  selectedAuthorizationConsents: AuthorizationSource[],
  serviceTypeList: HealthcareService[]
): boolean {
  const selectedServiceTypeIds = selectedAuthorizationConsents
    .map((item) => extractServiceTypesFromAuthorization(serviceTypeList, item.resource))
    .flatMap((serviceTypes) => serviceTypes.map((serviceType) => serviceType.id))

  const hasDuplicates = selectedServiceTypeIds.some(
    (item, index) => selectedServiceTypeIds.indexOf(item) !== index
  )

  return !hasDuplicates
}

export const getAuthorizationSources = (
  authorizationInfoReferences: (Reference | null)[],
  orderList: ServiceRequest[] = [],
  authorizationConsents: BaseConsentFragment[] = []
): AssessmentAuthorizationSource[] =>
  authorizationInfoReferences
    .map((reference) => {
      if (!reference) return null

      const [sourceType] = reference.reference?.split('/') ?? []

      switch (sourceType) {
        case 'ServiceRequest':
          return orderList.find((order) => `ServiceRequest/${order.id}` === reference.reference)
        case 'Consent':
          return authorizationConsents?.find(
            (consent) => `Consent/${consent.id}` === reference.reference
          )
        default:
          return null
      }
    })
    .filter((item): item is AssessmentAuthorizationSource => !!item)
